import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storesApi, accountsApi, healthApi } from '../services/api';
import type { StoreWithPriority, StoreUpdateRequest, StoreProduct } from 'shared';
import { formatCurrency, dollarsToCents } from 'shared';

interface Props {
  store: StoreWithPriority;
  onClose: () => void;
}

function ProductsSection({ storeId }: { storeId: string }) {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['store-products', storeId],
    queryFn: () => storesApi.products.list(storeId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['store-products', storeId] });

  const createMutation = useMutation({
    mutationFn: () => storesApi.products.create(storeId, {
      product_name: newName.trim(),
      retail_price_cents: newPrice ? dollarsToCents(parseFloat(newPrice)) : undefined,
    }),
    onSuccess: () => {
      setNewName('');
      setNewPrice('');
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      storesApi.products.update(storeId, id, {
        product_name: editName.trim(),
        retail_price_cents: editPrice ? dollarsToCents(parseFloat(editPrice)) : null,
      }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => storesApi.products.delete(storeId, id),
    onSuccess: invalidate,
  });

  const startEdit = (p: StoreProduct) => {
    setEditingId(p.id);
    setEditName(p.product_name);
    setEditPrice(p.retail_price_cents != null ? (p.retail_price_cents / 100).toFixed(2) : '');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Products on Floor</label>
        <span className="text-xs text-gray-400">{products.length} item{products.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Existing products */}
      {isLoading ? (
        <p className="text-sm text-gray-400 italic">Loading...</p>
      ) : products.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No products added yet.</p>
      ) : (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-100 mb-3">
          {products.map((p) =>
            editingId === p.id ? (
              <div key={p.id} className="p-2.5 space-y-2 bg-blue-50">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Product name"
                  className="block w-full text-sm rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    placeholder="0.00"
                    className="block w-full text-sm rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="text-xs px-2 py-1 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => updateMutation.mutate({ id: p.id })}
                    disabled={!editName.trim() || updateMutation.isPending}
                    className="text-xs px-2 py-1 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div key={p.id} className="flex items-center gap-2 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{p.product_name}</p>
                  {p.retail_price_cents != null && (
                    <p className="text-xs text-gray-500">
                      {formatCurrency(p.retail_price_cents)} retail
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  className="shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Edit"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate(p.id)}
                  disabled={deleteMutation.isPending}
                  className="shrink-0 p-1 text-gray-400 hover:text-red-500 rounded"
                  title="Remove"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )
          )}
        </div>
      )}

      {/* Add new product */}
      <div className="border border-dashed border-gray-300 rounded-md p-3 space-y-2">
        <p className="text-xs font-medium text-gray-500">Add product</p>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Product name"
          className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 flex-1">
            <span className="text-gray-500 text-sm">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              placeholder="Retail price"
              className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={!newName.trim() || createMutation.isPending}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 shrink-0"
          >
            {createMutation.isPending ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StoreEditDrawer({ store, onClose }: Props) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: store.name,
    chain_name: store.chain_name || '',
    account_id: store.account_id || '',
    address_line1: store.address_line1 || '',
    city: store.city || '',
    state: store.state || '',
    postal_code: store.postal_code || '',
    merchandiser_motion: store.merchandiser_motion,
    merchandiser_stationary: store.merchandiser_stationary,
    merchandiser_casegoods: store.merchandiser_casegoods,
    notes: store.notes || '',
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['store-health', store.id],
    queryFn: () => healthApi.getStoreHealth(store.id),
    enabled: !!store.place_id,
  });

  const refreshHealthMutation = useMutation({
    mutationFn: () => healthApi.refreshStoreHealth(store.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-health', store.id] });
      queryClient.invalidateQueries({ queryKey: ['stores-health'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: StoreUpdateRequest) => storesApi.update(store.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => storesApi.delete(store.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      ...formData,
      account_id: formData.account_id || null,
    });
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-[1100]">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full sm:max-w-md bg-white shadow-xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between shrink-0">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Edit Store</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Store Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Chain Name</label>
                <input
                  type="text"
                  value={formData.chain_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, chain_name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Parent Account</label>
                <select
                  value={formData.account_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, account_id: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">None (Standalone)</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  value={formData.address_line1}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address_line1: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">ZIP</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData((prev) => ({ ...prev, postal_code: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Merchandiser Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Merchandiser Types</label>
                <div className="space-y-2">
                  {[
                    { key: 'merchandiser_motion', label: 'Motion' },
                    { key: 'merchandiser_stationary', label: 'Stationary' },
                    { key: 'merchandiser_casegoods', label: 'Casegoods' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData[key as keyof typeof formData] as boolean}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              {/* Priority Info (read-only) */}
              {store.priority && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Calculated Values</h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-gray-500">Account Size</dt>
                    <dd className="text-gray-900 capitalize">{store.priority.account_size || '-'}</dd>
                    <dt className="text-gray-500">YTD Revenue</dt>
                    <dd className="text-gray-900">{formatCurrency(store.priority.ytd_revenue_cents)}</dd>
                    <dt className="text-gray-500">Visit Duration</dt>
                    <dd className="text-gray-900">{store.priority.visit_duration_minutes} min</dd>
                    <dt className="text-gray-500">Target Visits/Month</dt>
                    <dd className="text-gray-900">{store.priority.target_visits_per_month}</dd>
                  </dl>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between pt-2 pb-2">
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove Store
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>

            {/* Store Health */}
            {store.place_id && (
              <div className="border-t border-gray-200 pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Store Health</h3>
                  <button
                    type="button"
                    onClick={() => refreshHealthMutation.mutate()}
                    disabled={refreshHealthMutation.isPending || healthLoading}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    <svg
                      className={`w-3.5 h-3.5 ${refreshHealthMutation.isPending ? 'animate-spin' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>

                {healthLoading ? (
                  <p className="text-xs text-gray-400">Loading health data...</p>
                ) : health ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className={`text-2xl font-bold ${
                        (health.health_score ?? 0) >= 80 ? 'text-green-600' :
                        (health.health_score ?? 0) >= 60 ? 'text-lime-600' :
                        (health.health_score ?? 0) >= 40 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {health.health_score ?? '—'}
                      </div>
                      <div>
                        <div className={`text-sm font-medium ${
                          health.health_trend === 'growing' ? 'text-green-600' :
                          health.health_trend === 'declining' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {health.health_trend === 'growing' ? '↑ Growing' :
                           health.health_trend === 'declining' ? '↓ Declining' : '→ Stable'}
                        </div>
                        {health.last_enriched_at && (
                          <div className="text-xs text-gray-400">
                            Updated {new Date(health.last_enriched_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    {health.google_rating != null && (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-gray-500">Rating</div>
                          <div className="font-semibold text-gray-900">
                            ★ {health.google_rating.toFixed(1)}
                            {health.google_review_count != null && (
                              <span className="text-gray-400 font-normal"> ({health.google_review_count.toLocaleString()})</span>
                            )}
                          </div>
                        </div>
                        {health.google_review_velocity != null && (
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-gray-500">Reviews/mo</div>
                            <div className="font-semibold text-gray-900">{health.google_review_velocity}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-xs text-gray-400 mb-2">No health data yet</p>
                    <button
                      type="button"
                      onClick={() => refreshHealthMutation.mutate()}
                      disabled={refreshHealthMutation.isPending}
                      className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {refreshHealthMutation.isPending ? 'Fetching...' : 'Fetch Health Data'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Products — lives outside the form so its own buttons don't submit */}
            <div className="border-t border-gray-200 pt-4 mt-2">
              <ProductsSection storeId={store.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
